import moment from 'moment'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { checkPerm } from 'utils/permManager'
import { capturePosthogEvent } from 'utils/posthog'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualFeatureFlags,
  selectActualPermissions,
  selectIsActualCompanyDataReady,
} from 'containers/App/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'
import { CAMPAIGN_TYPES_VALUES } from '../constants'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../WizardCampaign/steps/TrainingModuleStep/constants'
import { DEFAULT_SIMULATION_PROPERTIES } from '../WizardCampaign/WizardSimulationCampaign/constants'
import {
  DEFAULT_TRAINING_PROPERTIES,
  TRAINING_EVENTS,
} from '../WizardCampaign/WizardTrainingCampaign/constants'

import { EMERGENCY_STOP_REQUEST_ASYNC } from './CampaignReport/constants'
import {
  CAMPAIGN_TYPE_FILTER_CHANGE,
  CANCEL_CAMPAIGN_ASYNC,
  COPY_CAMPAIGN_ASYNC,
  DATA_REQUEST_ASYNC,
  DATE_FILTER_CHANGE,
  DELETE_CAMPAIGN_ASYNC,
  END_CAMPAIGN_ASYNC,
  GET_RECOMMENDED_CAMPAIGNS,
  LANGUAGES_FILTER_CHANGE,
  LAUNCH_TYPE_FILTER_CHANGE,
  PAGINATE_CHANGE,
  SAVE_DRAFT_ASYNC,
  SEARCH_CHANGE,
  SORT_PROPERTY_CHANGE,
  STATUS_FILTER_CHANGE,
  TOGGLE_ACTIVITY_STATUS_ASYNC,
} from './constants'
import {
  selectCampaignsSearchFilter,
  selectCampaignTypeFilter,
  selectData,
  selectDateFilter,
  selectLanguagesFilter,
  selectLaunchTypeFilter,
  selectPage,
  selectSortProperty,
  selectStatusFilter,
} from './selectors'

const getCampaignDraft = (campaignData) => {
  const CAMPAIGNS_MAP = {
    [CAMPAIGN_TYPES_VALUES.SIMULATION]: {
      endDate: moment(campaignData.endDate).format('YYYY-MM-DD'),
      closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
      sendRemindersUntil: new Date(campaignData.closeTime).toISOString(),
      scenariosIds: [campaignData.data.scenario?.id],
      landingPageId: campaignData.data.landingPageId,
      ...DEFAULT_SIMULATION_PROPERTIES,
    },
    [CAMPAIGN_TYPES_VALUES.TRAINING]: {
      trainingCampaignData: {
        subject: campaignData.data.subject,
        message: campaignData.data.message,
        fromEmail: campaignData.data.fromEmail,
      },
      closeTime: moment(campaignData.endDate).format('YYYY-MM-DDTHH:mm[Z]'),
      sendRemindersUntil: new Date(campaignData.endDate).toISOString(),
      ...DEFAULT_TRAINING_PROPERTIES,
    },
  }

  return CAMPAIGNS_MAP[campaignData.flowType]
}

// The list endpoint answers 403 for these two cases, so skip the request and
// surface the same error shape the page already renders (no Sentry noise).
// Company access (PartnerSwapPermission) cannot be mirrored client-side.
const SAT_NOT_ALLOWED_DETAIL = 'SAT is not allowed in the current plan'
const NO_ACCESS_DETAIL = "You don't have permission to view campaigns"

const getListBlockedError = (state) => {
  // Selected MSP tenant not loaded yet: the entitlements read here would be the partner's own.
  if (!selectIsActualCompanyDataReady(state)) return null
  if (selectActualFeatureFlags(state).PHISHING_SIMULATION_ALLOWED === false)
    return SAT_NOT_ALLOWED_DETAIL
  // A loaded company can omit `permissions`; checkPerm throws on undefined and
  // the list request never gets the no-access error the page already renders.
  if (!checkPerm(selectActualPermissions(state) || {}, ['edit_campaigns'])) return NO_ACCESS_DETAIL
  return null
}

const fetchCampaignsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      CAMPAIGN_TYPE_FILTER_CHANGE,
      LANGUAGES_FILTER_CHANGE,
      STATUS_FILTER_CHANGE,
      LAUNCH_TYPE_FILTER_CHANGE,
      DATE_FILTER_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      PAGINATE_CHANGE,
      SEARCH_CHANGE,
      COPY_CAMPAIGN_ASYNC.SUCCESS,
      CANCEL_CAMPAIGN_ASYNC.SUCCESS,
      END_CAMPAIGN_ASYNC.SUCCESS,
      DELETE_CAMPAIGN_ASYNC.SUCCESS,
      EMERGENCY_STOP_REQUEST_ASYNC.SUCCESS,
      SORT_PROPERTY_CHANGE
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.awarenessCenter.campaigns)
      return true
    }),
    mergeMap(() => {
      const state = state$.value
      const blockedDetail = getListBlockedError(state)

      if (blockedDetail)
        return of(
          DATA_REQUEST_ASYNC.onError({
            title: 'Error',
            detail: blockedDetail,
            status: 403,
            expectedErrorCodes: [403],
            response: { detail: blockedDetail },
          })
        )

      return api.awarenessCenter
        .getCampaigns({
          page: selectPage(state) || 1,
          sortProperty: selectSortProperty(state),
          campaignTypeFilter: selectCampaignTypeFilter(state)?.map(({ value }) => Number(value)),
          languagesFilter: selectLanguagesFilter(state)?.map(({ value }) => Number(value)),
          statusFilter: selectStatusFilter(state)?.map(({ value }) => Number(value)),
          launchTypeFilter: selectLaunchTypeFilter(state),
          dateFilter: getRangeFromDateFilter(selectDateFilter(state)),
          search: selectCampaignsSearchFilter(state),
          companyId: selectCompanyFilter(state)?.value,
          partner: !!state.app.data.partner,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              DATA_REQUEST_ASYNC.onSuccess({
                data: response,
              })
            )
          ),
          catchError((error) => of(epicsErrorHandler(DATA_REQUEST_ASYNC.onError, error, [403]))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

export const copyCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(COPY_CAMPAIGN_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.copyCampaign(action.payload.id, action.payload.companiesIds).pipe(
        tap(() => {
          const source = selectData(state$.value).items?.find(({ id }) => id === action.payload.id)

          // The list row carries only the flag, not the video count.
          if (source?.is_playlist === true) capturePosthogEvent(TRAINING_EVENTS.PLAYLIST_DUPLICATED)
        }),
        map((response) => COPY_CAMPAIGN_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(COPY_CAMPAIGN_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(COPY_CAMPAIGN_ASYNC.CANCEL)))
      )
    )
  )

const cancelCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CANCEL_CAMPAIGN_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.cancelCampaign(action.payload.id).pipe(
        map(() => CANCEL_CAMPAIGN_ASYNC.onSuccess()),
        catchError((error) => of(epicsErrorHandler(CANCEL_CAMPAIGN_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(CANCEL_CAMPAIGN_ASYNC.CANCEL)))
      )
    )
  )

const endCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(END_CAMPAIGN_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.endCampaign(action.payload.id).pipe(
        map(() => END_CAMPAIGN_ASYNC.onSuccess()),
        catchError((error) => of(epicsErrorHandler(CANCEL_CAMPAIGN_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(END_CAMPAIGN_ASYNC.CANCEL)))
      )
    )
  )

const deleteCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_CAMPAIGN_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter.deleteCampaign(action.payload.id).pipe(
        map(() => DELETE_CAMPAIGN_ASYNC.onSuccess()),
        catchError((error) => of(epicsErrorHandler(DELETE_CAMPAIGN_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(DELETE_CAMPAIGN_ASYNC.CANCEL)))
      )
    )
  )

const getRecommendedCampaignsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_RECOMMENDED_CAMPAIGNS.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.awarenessCenter.campaigns)
      return true
    }),
    mergeMap(() =>
      api.awarenessCenter
        .getRecommendedCampaigns(
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map((response) =>
            GET_RECOMMENDED_CAMPAIGNS.onSuccess({
              data: response,
            })
          ),
          catchError((error) => of(epicsErrorHandler(GET_RECOMMENDED_CAMPAIGNS.onError, error))),
          takeUntil(action$.pipe(ofType(GET_RECOMMENDED_CAMPAIGNS.CANCEL)))
        )
    )
  )

const createRecommendedCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_DRAFT_ASYNC.INITIATE),
    mergeMap((action) => {
      const campaignData = state$.value.awarenessCenter.campaigns.recommendedPreviewData
      const participantsChoices = {}
      const trainingIds =
        campaignData.trainingVideo.id === AUTOMATED_LEVEL_BASED_TRAINING.id
          ? []
          : [campaignData.trainingVideo.id]

      campaignData.featuredGroups.forEach((group) => {
        participantsChoices[group] = [group]
      })

      return api.wizardCampaign
        .saveCampaign(state$.value.partnerOptions.selectedCompany?.value, {
          campaignTemplateId: action.payload.id,
          defaultLocaleId: action.payload.defaultLocaleId,
          participantsChoices,
          flowType: campaignData.flowType,
          name: campaignData.name,
          deliveryType: campaignData.deliveryType,
          participantsExcludeChoicesAutoFill: true,
          scheduleTime: moment(campaignData.launchDate).format('YYYY-MM-DDTHH:mm'),
          trainingIds,
          trainingReminders: [],
          ...getCampaignDraft(campaignData),
          sendManagerReportEveryXDays: null,
        })
        .pipe(
          map(({ response }) => SAVE_DRAFT_ASYNC.onSuccess({ id: response.id })),
          catchError((error) => of(epicsErrorHandler(SAVE_DRAFT_ASYNC.onError, error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

const toggleActivityStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(TOGGLE_ACTIVITY_STATUS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .toggleActivityStatus(action.payload.id, selectCompanyFilter(state$.value)?.value, {
          status: action.payload.status,
        })
        .pipe(
          map(() => TOGGLE_ACTIVITY_STATUS_ASYNC.onSuccess()),
          catchError((error) => of(epicsErrorHandler(TOGGLE_ACTIVITY_STATUS_ASYNC.onError, error))),
          takeUntil(action$.pipe(ofType(TOGGLE_ACTIVITY_STATUS_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  fetchCampaignsEpic,
  copyCampaignEpic,
  cancelCampaignEpic,
  endCampaignEpic,
  deleteCampaignEpic,
  getRecommendedCampaignsEpic,
  createRecommendedCampaignEpic,
  toggleActivityStatusEpic
)
